import { useIntl } from '@atlassian/jira-intl';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { SPRINT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import type { CardId } from '../../../../../../model/types.tsx';
import { cardFocus } from '../../../../../../state/actions/cards/focus/index.tsx';
import { cardSingleSelect } from '../../../../../../state/actions/cards/selection/index.tsx';
import { useBacklogDispatch } from '../../../../../../state/index.tsx';
import { useSimplyMoveCards } from '../../../../../../state/state-hooks/use-simply-move-cards/index.tsx';
import { RANK_MENU_DOWN_ID, RANK_MENU_UP_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';

/**
 * Menu items for ranking issues up and down their current position.
 */
export function useRankIssueItems({
	issueId,
	onScreenReaderAnnounce,
}: {
	issueId: CardId;
	onScreenReaderAnnounce?: (message: string) => void;
}): MenuNode[] {
	const { formatMessage } = useIntl();
	const moveCards = useSimplyMoveCards();
	const dispatch = useBacklogDispatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = (type: 'rank-up' | 'rank-down') => {
		moveCards({
			source: {
				cardId: issueId,
			},
			location: {
				type,
			},
		});

		dispatch(cardSingleSelect(issueId));
		dispatch(cardFocus(issueId));

		getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(FIELD_TYPE_MAP[SPRINT_TYPE], {
			analytics: createAnalyticsEvent({}),
			attributes: {
				fieldType: SPRINT_TYPE,
				hasRankChanged: true,
			},
		});
	};
	const onClickRankUp = () => onClick('rank-up');
	const onClickRankDown = () => onClick('rank-down');

	return [
		{
			type: MenuNodeType.Item,
			id: RANK_MENU_UP_ID,
			label: formatMessage(messages.up),
			onClick: () => {
				onScreenReaderAnnounce?.(formatMessage(messages.movedUp));
				onClickRankUp();
			},
		},
		{
			type: MenuNodeType.Item,
			id: RANK_MENU_DOWN_ID,
			label: formatMessage(messages.down),
			onClick: () => {
				onScreenReaderAnnounce?.(formatMessage(messages.movedDown));
				onClickRankDown();
			},
		},
	];
}
